import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const MessageCounter = ({ token, pageId }) => {
  const [dmCount, setDmCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [maxLimit, setMaxLimit] = useState(10000); // Default fallback
  const navigate = useNavigate();

  useEffect(() => {
    if (token && pageId) {
      fetchCounts(token, pageId);
    }
  }, [token, pageId]);

  const fetchCounts = async (token, pageId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/report/successful-messages-count?pageId=${pageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setDmCount(response.data.count || 0);
        setCommentCount(response.data.commentCount || 0);
        if (response.data.maxLimit) {
          setMaxLimit(response.data.maxLimit);
        }
      }
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const RenderCounter = ({ label, count, limit, colorClass, shadowClass }) => {
    const progress = Math.min((count / limit) * 100, 100);
    const percentage = Math.round(progress);
    const isLimitReached = count >= limit;
    const isNearLimit = !isLimitReached && progress > 80;

    return (
        <div className="flex flex-col w-full min-w-[180px]">
          <div className="flex items-center justify-between mb-1.5 gap-2">
            <span className="text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] whitespace-nowrap">
              {label}
            </span>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded whitespace-nowrap transition-all duration-300 ${
              isLimitReached 
                ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30" 
                : isNearLimit
                  ? "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30"
                  : `${colorClass} bg-opacity-10`
            }`}>
              {limit >= 1000000 ? "Unlimited" : `${percentage}%`}
            </span>
          </div>
          
          {limit < 1000000 && (
            <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-700 ease-out ${
                  isLimitReached
                    ? "bg-gradient-to-r from-red-400 to-red-600 shadow-[0_0_8px_rgba(239,68,68,0.4)]"
                    : isNearLimit
                      ? "bg-gradient-to-r from-orange-400 to-orange-600 shadow-[0_0_8px_rgba(249,115,22,0.4)]"
                      : `${shadowClass}`
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          
          <div className="mt-1 flex justify-between items-center text-[9px] font-medium text-gray-500 dark:text-gray-400 opacity-60">
             <span>{count.toLocaleString()} sent</span>
             {limit < 1000000 && <span>quota: {limit.toLocaleString()}</span>}
          </div>
        </div>
    );
  };

  return (
    <div
      onClick={() => navigate("/dashboard/billing")}
      className="flex items-center gap-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-5 py-2.5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-lg group hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-300 cursor-pointer min-w-[400px]"
    >
      <RenderCounter 
        label="Monthly DM Limit" 
        count={dmCount} 
        limit={maxLimit} 
        colorClass="text-primary-600 dark:text-primary-400"
        shadowClass="bg-gradient-to-r from-primary-400 to-primary-600 shadow-[0_0_8px_rgba(14,165,233,0.4)]"
      />
      
      <div className="w-[1px] h-10 bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

      <RenderCounter 
        label="Monthly Comment Limit" 
        count={commentCount} 
        limit={maxLimit} 
        colorClass="text-green-600 dark:text-green-400"
        shadowClass="bg-gradient-to-r from-green-400 to-green-600 shadow-[0_0_8px_rgba(34,197,94,0.4)]"
      />
    </div>
  );
};

export default MessageCounter;

